import { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

interface Props {
  beforeSrc: string;
  afterSrc: string;
  alt?: string;
  afterObjectPosition?: string;
  beforeObjectPosition?: string;
  /** When true, the comparator fills its parent's height instead of using a fixed aspect ratio. */
  fill?: boolean;
}

export const BeforeAfter = ({
  beforeSrc,
  afterSrc,
  alt = "",
  afterObjectPosition = "center",
  beforeObjectPosition = "center",
  fill = false,
}: Props) => {
  const [pos, setPos] = useState(50);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    setLoaded(false);
  }, [beforeSrc, afterSrc]);

  return (
    <div className={`relative w-full overflow-hidden rounded-2xl bg-muted shadow-soft ${fill ? "h-full" : ""}`}>
      <div className={`relative w-full ${fill ? "h-full" : "aspect-[4/5]"}`}>
        <img
          src={afterSrc}
          alt={`After ${alt}`}
          className="absolute inset-0 h-full w-full object-cover"
          style={{ objectPosition: afterObjectPosition }}
          onLoad={() => setLoaded(true)}
        />
        {/* Before image: shown with object-contain so the user's original
            upload is displayed at its native aspect with no cropping or
            upscaling — matching the clarity of the outfit-photo upload. */}
        <img
          src={beforeSrc}
          alt={`Before ${alt}`}
          className="absolute inset-0 h-full w-full object-contain"
          style={{
            objectPosition: beforeObjectPosition,
            clipPath: `inset(0 ${100 - pos}% 0 0)`,
            WebkitClipPath: `inset(0 ${100 - pos}% 0 0)`,
            imageRendering: "auto",
          }}
          decoding="async"
          loading="eager"
        />

        {/* slider line */}
        <div
          className="absolute inset-y-0 z-10 w-0.5 bg-white shadow-elevated"
          style={{ left: `${pos}%`, transform: "translateX(-50%)" }}
        >
          <div className="absolute top-1/2 left-1/2 flex h-10 w-10 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-glow">
            <ChevronLeft className="h-4 w-4" />
            <ChevronRight className="h-4 w-4 -ml-1" />
          </div>
        </div>

        {/* labels */}
        <span className="absolute left-3 top-3 rounded-full bg-black/60 px-2.5 py-1 text-xs font-semibold text-white">
          Before
        </span>
        <span className="absolute right-3 top-3 rounded-full bg-primary px-2.5 py-1 text-xs font-semibold text-primary-foreground">
          After
        </span>

        <input
          type="range"
          min={0}
          max={100}
          value={pos}
          onChange={(e) => setPos(Number(e.target.value))}
          aria-label="Comparison slider"
          className="absolute inset-0 z-20 h-full w-full cursor-ew-resize opacity-0"
        />

        {!loaded && (
          <div className="absolute inset-0 bg-muted" />
        )}
      </div>
    </div>
  );
};
